import { faChartLine } from '@fortawesome/pro-regular-svg-icons';
import { faChartColumn } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IDeploymentAndValueStatsSavingsAggregate } from '@kcftech/prometheus-client';
import { BarChartLegend } from 'pages/Dashboard/components/BarChartLegend';
import { interpolateZeroes } from 'pages/Dashboard/components/chart_data_utils';
import { ScrollableBarChart } from 'pages/Dashboard/components/ScrollableBarChart';
import {
    SegmentButton,
    SegmentedControl,
    StyledRechartsResponsiveContainer,
    ViewToggleContainer,
} from 'pages/Dashboard/components/styled';
import { ReactElement, useMemo, useState } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { RenderableText } from 'recharts/types/component/Text';
import { tokens } from 'styles/tokens';
import { sortBy } from 'utils/array';

/**
 * Parses a month string in "YYYY-MM" format to a Date object.
 * Appends '-01' to create a valid date at the first day of the month.
 * @param monthKey - Month string in "YYYY-MM" format (e.g., "2025-12")
 * @returns Date object or null if invalid
 */
export function parseMonthKey(monthKey: string): Date | null {
    const date = new Date(monthKey + '-01');
    return isNaN(date.getTime()) ? null : date;
}

const formatCurrency = <T extends RenderableText>(value: T): T | string => {
    if (typeof value !== 'number') {
        return value;
    }
    if (value >= 1_000_000) {
        return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
        return `$${(value / 1_000).toFixed(1)}K`;
    }
    return `$${value.toLocaleString()}`;
};

const monthTickFormatter = (tick: string | number | Date): string => {
    // Handle date strings in format "YYYY-MM"
    const date = parseMonthKey(String(tick));
    if (!date) {
        return String(tick);
    }
    return date.toLocaleString('default', { month: 'short' });
};

const createRenderQuarterTick =
    (strokeColor: string) =>
    (tickProps: {
        x: number;
        y: number;
        payload: { value: string; offset: number };
        width: number;
        visibleTicksCount: number;
    }): ReactElement => {
        const { x, y, payload, width, visibleTicksCount } = tickProps;
        const { value, offset } = payload;
        const date = parseMonthKey(value);
        if (!date) {
            return <></>;
        }

        const month = date.getMonth();
        const quarterNo = Math.floor(month / 3) + 1;

        if (month % 3 === 1) {
            // place quarter text near the middle
            return (
                <text
                    x={x + width / visibleTicksCount / 2 - offset}
                    y={y - 4}
                    textAnchor="middle"
                >
                    {`Q${quarterNo}`}
                </text>
            );
        }

        const isLast = month === 11;

        if (month % 3 === 0 || isLast) {
            const pathX =
                Math.floor(isLast ? x - offset + width / visibleTicksCount : x - offset) +
                0.5;

            return <path d={`M${pathX},${y - 4}v${-35}`} stroke={strokeColor} />;
        }
        return <></>;
    };

const createRenderYearTick =
    (strokeColor: string, isLongPeriod: boolean = false) =>
    (tickProps: {
        x: number;
        y: number;
        payload: { value: string; offset: number };
        width: number;
        visibleTicksCount: number;
    }): React.ReactElement => {
        const { x, y, payload, width, visibleTicksCount } = tickProps;
        const { value, offset } = payload;
        const date = parseMonthKey(value);
        if (!date) {
            return <></>;
        }

        const month = date.getMonth();
        const year = date.getFullYear();

        // Show year label only in June (month index 5)
        if (month === 5) {
            return (
                <text
                    x={x + width / visibleTicksCount / 2 - offset}
                    y={y}
                    textAnchor="middle"
                >
                    {year}
                </text>
            );
        }

        // Draw vertical line at year boundaries (January)
        if (month === 0) {
            const pathX = Math.floor(x - offset) + 0.5;
            // Shorter line for long periods (only extends through quarter axis)
            const lineHeight = isLongPeriod ? -35 : -55;
            return (
                <path
                    d={`M${pathX},${y - 12}v${lineHeight}`}
                    stroke={strokeColor}
                    strokeWidth={1.5}
                />
            );
        }

        return <></>;
    };

/**
 * Simple quarter tick formatter for when quarters are the primary axis
 */
const quarterTickFormatter = (tick: string | number | Date): string => {
    const date = parseMonthKey(String(tick));
    if (!date) {
        return String(tick);
    }
    const month = date.getMonth();
    const quarterNo = Math.floor(month / 3) + 1;
    return `Q${quarterNo}`;
};

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

type ChartViewType = 'bar' | 'line';

export function ValueOverTime({
    data,
}: {
    /** key is a month identifier like 2025-12 */
    data: IDeploymentAndValueStatsSavingsAggregate[];
}) {
    const [viewType, setViewType] = useState<ChartViewType>('bar');

    const renderQuarterTick = useMemo(
        () => createRenderQuarterTick(tokens.nav.border),
        [],
    );

    const renderYearTick = useMemo(
        () => createRenderYearTick(tokens.text.secondary, false),
        [],
    );

    const renderYearTickLongPeriod = useMemo(
        () => createRenderYearTick(tokens.text.secondary, true),
        [],
    );

    const mappedData = useMemo(() => {
        const sortedData = sortBy(
            data.filter((d) => {
                if (!d.Key || Number.isNaN(d.MonetaryValue)) {
                    return false;
                }
                return parseMonthKey(d.Key) !== null;
            }),
            (d) => d.Key,
        );

        return interpolateZeroes({
            sortedData,
            keyExtractor: (d) => parseMonthKey(d.Key)?.getTime() ?? null,
            keyFormatter: (dateNum) => {
                const d = new Date(dateNum);
                const startOfMonth = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
                const startOfNextMonth = Date.UTC(
                    d.getUTCFullYear(),
                    d.getUTCMonth() + 1,
                    1,
                );
                // snap to closer of startOfMonth or startOfNextMonth
                const snapped = new Date(
                    dateNum - startOfMonth < startOfNextMonth - dateNum
                        ? startOfMonth
                        : startOfNextMonth,
                );
                const year = snapped.getUTCFullYear();
                const month = String(snapped.getUTCMonth() + 1).padStart(2, '0');
                return `${year}-${month}`;
            },
            createZeroEntry: (_key, formattedKey) => ({
                Key: formattedKey,
                MonetaryValue: 0,
                SavingsByUnit: [],
            }),
            expectedInterval: ONE_MONTH_MS,
            tolerance: ONE_MONTH_MS / 4,
        }).map((d) => ({
            date: d.Key,
            value: d.MonetaryValue,
        }));
    }, [data]);

    // Calculate if time span is greater than 2 years (24 months)
    const isLongTimePeriod = useMemo(() => {
        if (mappedData.length === 0) {
            return false;
        }
        const firstDate = parseMonthKey(mappedData[0].date);
        const lastDate = parseMonthKey(mappedData[mappedData.length - 1].date);
        if (!firstDate || !lastDate) {
            return false;
        }

        const monthsDiff =
            (lastDate.getFullYear() - firstDate.getFullYear()) * 12 +
            (lastDate.getMonth() - firstDate.getMonth());
        return monthsDiff > 24;
    }, [mappedData]);

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <ViewToggleContainer>
                <SegmentedControl>
                    <SegmentButton
                        $isActive={viewType === 'line'}
                        onClick={() => setViewType('line')}
                        aria-label="Line chart view"
                    >
                        <FontAwesomeIcon icon={faChartLine} />
                    </SegmentButton>
                    <SegmentButton
                        $isActive={viewType === 'bar'}
                        onClick={() => setViewType('bar')}
                        aria-label="Bar chart view"
                    >
                        <FontAwesomeIcon icon={faChartColumn} />
                    </SegmentButton>
                </SegmentedControl>
            </ViewToggleContainer>

            {viewType === 'bar' ? (
                <ScrollableBarChart
                    yAxisWidth={60}
                    dataLength={mappedData.length}
                    minBarWidthPercent={10}
                    startScrolledToEnd
                    legend={
                        <BarChartLegend
                            items={[
                                {
                                    label: 'Savings ($)',
                                    color: tokens.primary.default,
                                },
                            ]}
                        />
                    }
                >
                    {({ yAxisWidth }) => (
                        <BarChart data={mappedData}>
                            <defs>
                                <linearGradient
                                    id="valueOverTimeVerticalBarColor"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={tokens.primary.default}
                                        stopOpacity={0.75}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={tokens.primary.default}
                                        stopOpacity={0.5}
                                    />
                                </linearGradient>
                            </defs>

                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={monthTickFormatter}
                                height={34}
                            />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                tick={renderQuarterTick}
                                height={14}
                                scale="band"
                                xAxisId="quarter"
                            />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                tick={renderYearTick}
                                height={5}
                                scale="band"
                                xAxisId="year"
                            />
                            <YAxis width={yAxisWidth} tickFormatter={formatCurrency} />
                            <Tooltip
                                formatter={(value: number | undefined) =>
                                    value != null ? `$${value.toLocaleString()}` : ''
                                }
                            />

                            <Bar
                                dataKey="value"
                                fill={'url(#valueOverTimeVerticalBarColor)'}
                                name="Savings ($)"
                                label={{
                                    position: 'top',
                                    formatter: formatCurrency,
                                    fontSize: 11,
                                }}
                                radius={[5, 5, 0, 0]}
                            />
                        </BarChart>
                    )}
                </ScrollableBarChart>
            ) : (
                <div style={{ height: '100%', width: '100%' }}>
                    <StyledRechartsResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={mappedData}
                            margin={{
                                top: 20,
                                right: 20,
                                // Adjust bottom margin based on whether month axis is shown
                                bottom: isLongTimePeriod ? 25 : 40,
                                left: 10,
                            }}
                        >
                            <defs>
                                <linearGradient
                                    id="colorValue"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={tokens.primary.default}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={tokens.primary.default}
                                        stopOpacity={0.05}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" />
                            {/* Only show month labels if time period is <= 2 years */}
                            {!isLongTimePeriod && (
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={monthTickFormatter}
                                    height={34}
                                    type="category"
                                />
                            )}
                            {isLongTimePeriod ? (
                                <>
                                    {/* When time period > 2 years, use quarters as primary axis */}
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={quarterTickFormatter}
                                        height={30}
                                        interval={2}
                                        type="category"
                                    />
                                    {/* Year axis - aligned with same scale */}
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tick={renderYearTickLongPeriod}
                                        height={5}
                                        type="category"
                                        xAxisId="year"
                                    />
                                </>
                            ) : (
                                <>
                                    {/* When time period <= 2 years, use custom quarter rendering above months */}
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tick={renderQuarterTick}
                                        height={14}
                                        type="category"
                                        xAxisId="quarter"
                                    />
                                    {/* Year axis - aligned with same scale */}
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tick={renderYearTick}
                                        height={5}
                                        type="category"
                                        xAxisId="year"
                                    />
                                </>
                            )}
                            <YAxis width={60} tickFormatter={formatCurrency} />
                            <Tooltip
                                formatter={(value: number | undefined) =>
                                    value != null ? `$${value.toLocaleString()}` : ''
                                }
                            />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke={tokens.primary.default}
                                strokeWidth={2}
                                fill="url(#colorValue)"
                                name="Savings ($)"
                                dot={{ r: 4, fill: tokens.primary.default }}
                                activeDot={{ r: 6 }}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </StyledRechartsResponsiveContainer>
                    <div style={{ textAlign: 'center', paddingTop: '1em' }}>
                        <BarChartLegend
                            items={[
                                {
                                    label: 'Savings ($)',
                                    color: tokens.primary.default,
                                },
                            ]}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
